import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase';
import { DEFAULT_SYSTEM_INSTRUCTIONS } from '../constants';
import { MarkingRunData } from '../types';
import mammoth from 'mammoth';
import * as pdfjsLib from 'pdfjs-dist';

// Set up the worker source for PDF.js
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://esm.sh/pdfjs-dist@4.4.168/build/pdf.worker.mjs`;

const assessmentsCollection = collection(db, 'assessments');
const essaysCollection = collection(db, 'essays');

const handleFirestoreError = (error: any, context: string) => {
    console.error(`Firestore error during ${context}:`, error);
    let userMessage = `An error occurred with the database while ${context}.`;

    if (error?.code) {
        switch (error.code) {
            case 'permission-denied':
                userMessage = 'Database permission denied. Please check your Firestore security rules in the Firebase console.';
                break;
            case 'unauthenticated':
                userMessage = 'You are not authenticated. Please log in again.';
                break;
            case 'failed-precondition':
                userMessage = 'A database precondition failed. This can happen if a required index has not been created. Check the developer console for a link to create the necessary index in Firebase.';
                break;
            case 'not-found':
                userMessage = 'The database was not found. Please ensure you have created a Firestore database in "Native Mode" in your Firebase project console.';
                break;
            case 'unavailable':
                 userMessage = 'The database service is currently unavailable. Please try again later.';
                 break;
        }
    } else if (error?.message && (error.message.includes('400') || error.message.includes('Bad Request'))) {
         userMessage = 'Received a "Bad Request" from the database server. This commonly means the Firestore database has not been created yet. Please go to the Firestore section of your Firebase console and click "Create Database".';
    }

    throw new Error(userMessage);
};

export const getServerTimestamp = () => {
    return serverTimestamp();
};

// --- DATA SANITIZATION UTILITIES ---

/**
 * Recursively removes undefined values from objects and arrays to prevent Firestore errors
 * @param obj - The object to sanitize
 * @param convertUndefinedToNull - Whether to convert undefined values to null instead of removing them
 * @returns Sanitized object safe for Firestore
 */
const sanitizeForFirestore = (obj: any, convertUndefinedToNull: boolean = false): any => {
    if (obj === null || obj === undefined) {
        return convertUndefinedToNull ? null : undefined;
    }
    
    if (Array.isArray(obj)) {
        return obj.map(item => sanitizeForFirestore(item, convertUndefinedToNull))
                  .filter(item => item !== undefined);
    }
    
    if (obj instanceof Date) {
        return obj;
    }
    
    if (typeof obj === 'object') {
        const sanitized: any = {};
        
        for (const [key, value] of Object.entries(obj)) {
            const sanitizedValue = sanitizeForFirestore(value, convertUndefinedToNull);
            
            // Only include the property if it's not undefined
            if (sanitizedValue !== undefined) {
                sanitized[key] = sanitizedValue;
            }
        }
        
        return sanitized;
    }
    
    return obj;
};

/**
 * Sanitizes model result data before writing to Firestore
 * @param modelResult - The model result object to sanitize
 * @returns Sanitized model result safe for Firestore
 */
export const sanitizeModelResult = (modelResult: any): any => {
    // For model results, convert undefined to null instead of removing the properties
    // This prevents critical model result fields from being lost during sanitization
    return sanitizeForFirestore(modelResult, true);
};

// --- FILE EXTRACTION ---

/**
 * Extracts raw text from a DOCX file.
 */
async function extractTextFromDocx(file: File): Promise<string> {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value;
}

/**
 * Extracts raw text from a PDF file, page by page.
 */
async function extractTextFromPdf(file: File): Promise<string> {
    const arrayBuffer = await file.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdf = await loadingTask.promise;
    let fullText = '';

    for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => ('str' in item ? item.str : '')).join(' ');
        fullText += `--- Page ${i} ---\n${pageText}\n\n`;
    }

    return fullText.trim();
}

/**
 * Extracts raw text from a plain text file.
 */
async function extractTextFromTxt(file: File): Promise<string> {
    return file.text();
}

/**
 * Extracts raw text content from a supported file type.
 */
export async function extractTextFromFile(file: File): Promise<string> {
    const fileType = file.type;
    const fileName = file.name.toLowerCase();

    if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || fileName.endsWith('.docx')) {
        return extractTextFromDocx(file);
    }

    if (fileType === 'application/pdf' || fileName.endsWith('.pdf')) {
        return extractTextFromPdf(file);
    }
    
    if (fileType === 'text/plain' || fileName.endsWith('.txt')) {
        return extractTextFromTxt(file);
    }
    
    throw new Error(`Unsupported file type: ${fileType || 'unknown'}. Please upload a DOCX, PDF, or TXT file.`);
}

// --- Assessment CRUD ---
export const addAssessment = async (assessmentData: any, userId: string) => {
    try {
        // Get the next assessment number for this user
        const nextAssessmentNumber = await getNextAssessmentNumber(userId);

        // Firestore rejects undefined values (e.g. minWordCount/maxWordCount left blank);
        // convert them to null so optional fields can be stored and later cleared.
        const sanitizedData = sanitizeForFirestore(assessmentData, true);

        const docRef = await addDoc(assessmentsCollection, {
            ...sanitizedData,
            userId,
            assessmentNumber: nextAssessmentNumber,
            createdAt: serverTimestamp(),
        });
        return { id: docRef.id, ...sanitizedData, assessmentNumber: nextAssessmentNumber };
    } catch (error) {
        handleFirestoreError(error, 'adding assessment');
    }
};

export const getNextAssessmentNumber = async (userId: string): Promise<number> => {
    try {
        const q = query(
            assessmentsCollection, 
            where('userId', '==', userId)
        );
        const snapshot = await getDocs(q);
        
        // Find the highest existing assessment number
        let maxNumber = 0;
        snapshot.docs.forEach(doc => {
            const data = doc.data();
            if (data.assessmentNumber && data.assessmentNumber > maxNumber) {
                maxNumber = data.assessmentNumber;
            }
        });
        
        return maxNumber + 1;
    } catch (error) {
        handleFirestoreError(error, 'getting next assessment number');
        return 1; // Fallback to 1 if error
    }
};

export const assignNumbersToExistingAssessments = async (userId: string) => {
    try {
        // First get all assessments for the user (no orderBy to avoid composite index)
        const q = query(
            assessmentsCollection, 
            where('userId', '==', userId)
        );
        const snapshot = await getDocs(q);
        
        // Convert to array and sort by createdAt client-side
        const assessments = snapshot.docs
            .map(doc => ({ doc, data: doc.data() }))
            .filter(({ data }) => !data.assessmentNumber) // Only process assessments without numbers
            .sort((a, b) => {
                // Handle different createdAt formats safely
                let aTime = new Date(0);
                let bTime = new Date(0);
                
                try {
                    if (a.data.createdAt) {
                        if (typeof a.data.createdAt.toDate === 'function') {
                            aTime = a.data.createdAt.toDate();
                        } else if (a.data.createdAt.seconds) {
                            // Handle Firestore Timestamp-like objects
                            aTime = new Date(a.data.createdAt.seconds * 1000);
                        } else if (typeof a.data.createdAt === 'string' || typeof a.data.createdAt === 'number') {
                            aTime = new Date(a.data.createdAt);
                        }
                    }
                } catch (error) {
                    console.warn('Error parsing createdAt for assessment:', a.doc.id, error);
                }
                
                try {
                    if (b.data.createdAt) {
                        if (typeof b.data.createdAt.toDate === 'function') {
                            bTime = b.data.createdAt.toDate();
                        } else if (b.data.createdAt.seconds) {
                            // Handle Firestore Timestamp-like objects  
                            bTime = new Date(b.data.createdAt.seconds * 1000);
                        } else if (typeof b.data.createdAt === 'string' || typeof b.data.createdAt === 'number') {
                            bTime = new Date(b.data.createdAt);
                        }
                    }
                } catch (error) {
                    console.warn('Error parsing createdAt for assessment:', b.doc.id, error);
                }
                
                return aTime.getTime() - bTime.getTime();
            });
        
        if (assessments.length === 0) {
            return 0; // No assessments to update
        }
        
        const batch = writeBatch(db);
        let assessmentNumber = 1;
        
        // Find the highest existing assessment number to continue from
        const allAssessments = snapshot.docs.map(doc => doc.data());
        const maxExistingNumber = Math.max(0, ...allAssessments
            .map(data => data.assessmentNumber || 0)
            .filter(num => typeof num === 'number'));
        
        if (maxExistingNumber > 0) {
            assessmentNumber = maxExistingNumber + 1;
        }
        
        assessments.forEach(({ doc }) => {
            batch.update(doc.ref, { assessmentNumber });
            assessmentNumber++;
        });
        
        await batch.commit();
        return assessments.length; // Return the number of assessments updated
    } catch (error) {
        handleFirestoreError(error, 'assigning numbers to existing assessments');
        return 0;
    }
};

export const getAssessments = async (userId: string, showArchived: boolean = false) => {
    try {
        let q = query(
            assessmentsCollection, 
            where('userId', '==', userId),
            orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(q);
        const assessments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Filter assessments based on archived status
        return assessments.filter(assessment => {
            const assessmentWithArchive = assessment as { archived?: boolean };
            if (showArchived) {
                // When showing archived, only return assessments that are explicitly archived
                return assessmentWithArchive.archived === true;
            } else {
                // When showing active, only return assessments that are not archived
                return assessmentWithArchive.archived !== true;
            }
        });
    } catch (error) {
        handleFirestoreError(error, 'getting assessments');
    }
};

export const getAllAssessments = async (userId: string) => {
    try {
        const q = query(
            assessmentsCollection,
            where('userId', '==', userId),
            orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        handleFirestoreError(error, 'getting all assessments');
    }
};

export const updateAssessment = async (assessmentData: any) => {
    // createdAt is a Firestore Timestamp and must never be rewritten (the sanitizer
    // would flatten it to a plain object and break createdAt ordering).
    const { id, createdAt, ...data } = assessmentData;
    try {
        // Convert undefined to null (rather than dropping the key) so clearing an
        // optional field like minWordCount actually overwrites the stored value.
        await updateDoc(doc(db, 'assessments', id), sanitizeForFirestore(data, true));
    } catch (error) {
        handleFirestoreError(error, 'updating assessment');
    }
};

export const archiveAssessment = async (assessmentId: string, archive: boolean = true) => {
    try {
        await updateDoc(doc(db, 'assessments', assessmentId), { archived: archive });
    } catch (error) {
        handleFirestoreError(error, archive ? 'archiving assessment' : 'unarchiving assessment');
    }
};

export const deleteAssessment = async (assessmentId: string, userId: string) => {
    try {
        const q = query(
            essaysCollection,
            where('assessmentId', '==', assessmentId),
            where('userId', '==', userId)
        );
        const essaysSnapshot = await getDocs(q);
        
        const batch = writeBatch(db);
        essaysSnapshot.docs.forEach(doc => {
            batch.delete(doc.ref);
        });
        await batch.commit();
        
        await deleteDoc(doc(db, 'assessments', assessmentId));
    } catch (error) {
        handleFirestoreError(error, 'deleting assessment and its essays');
    }
};

// --- Essay CRUD ---
export const addEssay = async (userId: string, assessmentId: string, studentName: string, inputType: string) => {
    try {
        const docRef = await addDoc(essaysCollection, {
            userId,
            assessmentId,
            studentName,
            inputType,
            createdAt: serverTimestamp(),
            feedback: "Initializing...",
        });
        return { id: docRef.id, studentName };
    } catch (error) {
        handleFirestoreError(error, 'adding new essay');
    }
};

export const getEssay = async (essayId: string) => {
    try {
        const docRef = doc(db, 'essays', essayId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return { id: docSnap.id, ...docSnap.data() };
        }
        return null;
    } catch (error) {
        handleFirestoreError(error, `getting essay ${essayId}`);
    }
};

export const getEssaysForAssessment = async (userId: string, assessmentId: string) => {
    try {
        const q = query(
            essaysCollection,
            where('userId', '==', userId),
            where('assessmentId', '==', assessmentId),
            orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        handleFirestoreError(error, 'getting essays for assessment');
    }
};

export const updateEssay = async (essayId: string, data: any) => {
    try {
        // Sanitize the data to remove undefined values that would cause Firestore errors
        const sanitizedData = sanitizeForFirestore(data);
        await updateDoc(doc(db, 'essays', essayId), sanitizedData);
    } catch (error) {
        handleFirestoreError(error, `updating essay ${essayId}`);
    }
};

export const updateEssayWithModelResult = async (essayId: string, modelResult: any) => {
    try {
        console.log(`Saving model result for essay ${essayId} with model ${modelResult.modelId}:`, {
            modelId: modelResult.modelId,
            hasConsolidatedResults: !!modelResult.consolidatedMarkingResults,
            hasScore: !!modelResult.finalOverallScore,
            hasFeedback: !!modelResult.feedback
        });
        
        // Get the current essay to preserve existing model results
        const essayRef = doc(db, 'essays', essayId);
        const essaySnap = await getDoc(essayRef);
        
        let currentModelResults = {};
        if (essaySnap.exists()) {
            const essayData = essaySnap.data();
            currentModelResults = essayData.modelResults || {};
        }

        // Sanitize the model result to remove undefined values that would cause Firestore errors
        const sanitizedModelResult = sanitizeModelResult({
            ...modelResult,
            lastRunAt: modelResult.lastRunAt || serverTimestamp() // Use provided timestamp or fallback
        });

        // Update with the new model result
        const updatedModelResults = {
            ...currentModelResults,
            [modelResult.modelId]: sanitizedModelResult
        };

        // Sanitize the entire update payload
        const updatePayload = sanitizeForFirestore({
            modelResults: updatedModelResults,
            markedAt: serverTimestamp(),
        });

        await updateDoc(essayRef, updatePayload);
        
        console.log(`Successfully saved model result for essay ${essayId} with model ${modelResult.modelId}`);
    } catch (error) {
        console.error(`Failed to save model result for essay ${essayId}:`, error);
        handleFirestoreError(error, `updating essay ${essayId} with model result`);
    }
};

export const deleteEssay = async (essayId: string) => {
    try {
        await deleteDoc(doc(db, 'essays', essayId));
    } catch (error) {
        handleFirestoreError(error, `deleting essay ${essayId}`);
    }
};

export const deleteModelResult = async (essayId: string, modelId: string) => {
    try {
        // Get the current essay to access existing model results
        const essayRef = doc(db, 'essays', essayId);
        const essaySnap = await getDoc(essayRef);
        
        if (!essaySnap.exists()) {
            throw new Error(`Essay with ID ${essayId} not found`);
        }

        const essayData = essaySnap.data();
        const currentModelResults = essayData.modelResults || {};

        // Check if the model result exists
        if (!currentModelResults[modelId]) {
            throw new Error(`Model result for ${modelId} not found in essay ${essayId}`);
        }

        // Remove the specific model result
        const updatedModelResults = { ...currentModelResults };
        delete updatedModelResults[modelId];

        // Update the essay document - don't sanitize serverTimestamp()
        const updatePayload = {
            modelResults: sanitizeForFirestore(updatedModelResults),
            markedAt: serverTimestamp(), // Keep serverTimestamp() unsanitized
        };

        await updateDoc(essayRef, updatePayload);
    } catch (error) {
        handleFirestoreError(error, `deleting model result ${modelId} from essay ${essayId}`);
    }
};

// --- Storage ---
export const uploadEssayImage = async (userId: string, essayId: string, file: File) => {
    const filePath = `essays/${userId}/${essayId}/${Date.now()}_${file.name}`;
    const fileRef = ref(storage, filePath);
    try {
        await uploadBytes(fileRef, file);
        return await getDownloadURL(fileRef);
    } catch (error) {
        console.error("Error uploading image:", error);
        throw error;
    }
};

// --- UTILITY FUNCTIONS ---
const calculateMode = (arr: any[]): any => {
    if (!arr || arr.length === 0) return null;
    const modeMap: { [key: string]: number } = {};
    let maxCount = 0;
    let modes: any[] = [];
    arr.forEach(item => {
        modeMap[item] = (modeMap[item] || 0) + 1;
        if (modeMap[item] > maxCount) {
            maxCount = modeMap[item];
            modes = [item];
        } else if (modeMap[item] === maxCount && !modes.includes(item)) {
            modes.push(item);
        }
    });
    if (modes.length === 0) return null;
    if (modes.length > 1) {
        return modes.sort((a, b) => {
            const rangeA = parseBandRange(a);
            const rangeB = parseBandRange(b);
            if (!rangeA) return 1;
            if (!rangeB) return -1;
            return rangeA.min - rangeB.min;
        })[0];
    }
    return modes[0];
};

const trimmedMean = (arr: number[]): number => {
    if (!arr || arr.length === 0) return 0;
    if (arr.length < 3) {
        const sum = arr.reduce((a, b) => a + b, 0);
        return sum / arr.length;
    }
    const sorted = [...arr].sort((a, b) => a - b);
    const trimmed = sorted.slice(1, -1);
    const sum = trimmed.reduce((a, b) => a + b, 0);
    return sum / trimmed.length;
};

export const calculateStdDev = (arr: number[]): number => {
    if (!arr || arr.length < 2) return 0;
    const n = arr.length;
    const mean = arr.reduce((a, b) => a + b) / n;
    const variance = arr.reduce((a, b) => a + (b - mean) ** 2, 0) / (n - 1);
    return Math.sqrt(variance);
};

const clamp = (value: number, min: number, max: number): number => 
    Math.max(min, Math.min(value, max));

const normalizeCriterionName = (name: string): string => {
    if (typeof name !== 'string') return '';

    // Make criterion-name matching resilient across model/providers + teacher input.
    // Key goals:
    // - ignore leading "Criterion X:" prefixes
    // - normalize unicode punctuation (e.g., en-dash, curly quotes)
    // - remove punctuation differences (& vs and, colon, commas, brackets)
    // - collapse whitespace
    return name
        .replace(/^criterion\s*\d*\s*:\s*/i, '')
        .trim()
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[’']/g, "'")
        .replace(/[—–-]/g, '-')
        .replace(/[^a-z0-9\s-]/g, ' ') // drop punctuation but keep words/numbers
        .replace(/\s+/g, ' ')
        .trim();
};

// --- RUBRIC PARSING & TEXT PROCESSING ---

export const parseBandRange = (bandString: string) => {
    if (typeof bandString !== 'string') return null;
    const cleaned = bandString.replace(/[—–-]/g, '-');
    const match = cleaned.match(/(\d{1,3})\s*-\s*(\d{1,3})/);
    if (!match) return null;
    return { min: parseInt(match[1], 10), max: parseInt(match[2], 10) };
};

export const extractBandOrder = (rubric: string): string[] => {
    if (!rubric) return [];
    const regex = /(\d{1,3}\s?[—–-]\s?\d{1,3})/g;
    const matches = [...rubric.matchAll(regex)];
    const bands = [...new Set(matches.map(m => m[1].replace(/\s/g, '')))];
    return bands.sort((a, b) => {
        const rangeA = parseBandRange(a);
        const rangeB = parseBandRange(b);
        if (!rangeA || !rangeB) return 0;
        return rangeB.max - rangeA.max;
    });
};

const getRubricMaxScore = (rubric: string): number => {
    const bandOrder = extractBandOrder(rubric);
    if (bandOrder.length === 0) return 25;
    const topBandRange = parseBandRange(bandOrder[0]);
    return topBandRange ? topBandRange.max : 25;
};

const getMaxScoreForCriterion = (rubric: string, criterionName: string): number | null => {
    if (!rubric || !criterionName) return null;

    const safeCriterionName = criterionName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const criterionRegex = new RegExp(`(^|\\n)\\s*${safeCriterionName}.*?(?=(\\n\\s*[\\w\\s()]+:)|$)`, 'is');
    let criterionText = rubric;
    const criterionMatch = rubric.match(criterionRegex);
    if (criterionMatch && criterionMatch[0]) {
        criterionText = criterionMatch[0];
    }

    let marksMatch = criterionText.match(/\(\s*(\d+)\s*marks?\s*\)/i);
    if (marksMatch && marksMatch[1]) {
        return parseInt(marksMatch[1], 10);
    }

    marksMatch = criterionText.match(/out\s+of\s+(\d+)/i);
    if (marksMatch && marksMatch[1]) {
        return parseInt(marksMatch[1], 10);
    }

    marksMatch = criterionText.match(/(?:total marks|max score|marks|score)\s*:\s*(\d+)/i);
    if (marksMatch && marksMatch[1]) {
        return parseInt(marksMatch[1], 10);
    }

    marksMatch = criterionText.match(/\(\s*(\d+)\s*\)/);
    if (marksMatch && marksMatch[1]) {
        return parseInt(marksMatch[1], 10);
    }

    const bandMatches = [...criterionText.matchAll(/(\d+)\s*[—–-]\s*(\d+)/g)];
    if (bandMatches.length > 0) {
        const maxScores = bandMatches.map(m => parseInt(m[2], 10));
        return Math.max(...maxScores);
    }

    return null;
};

export const stripTeacherComments = (text: string): string => {
    if (!text) return '';
    return text
        .replace(/\[.*?\]/gs, '')
        .replace(/\*.*?\*/gs, '')
        .replace(/\([^)]*?(?:\?\)|\?\?\)|\?)+/gs, '')
        .trim();
};

// --- WORD COUNT UTILITIES ---

/**
 * Calculates word count from text, excluding common markdown/formatting elements
 */
export const calculateWordCount = (text: string): number => {
    if (!text) return 0;
    
    // Clean the text by removing:
    // - Page markers (--- Page X ---)
    // - Extra whitespace and line breaks
    // - Special formatting characters
    const cleanedText = text
        .replace(/---\s*Page\s+\d+\s*---/gi, '') // Remove page markers from PDFs
        .replace(/\s+/g, ' ') // Normalize whitespace
        .trim();
    
    if (!cleanedText) return 0;
    
    // Split by whitespace and filter out empty strings
    const words = cleanedText.split(/\s+/).filter(word => word.length > 0);
    return words.length;
};

/**
 * Updates an essay with word count in the database
 */
export const updateEssayWordCount = async (essayId: string, wordCount: number) => {
    try {
        await updateDoc(doc(db, 'essays', essayId), {
            wordCount,
            wordCountUpdatedAt: serverTimestamp()
        });
    } catch (error) {
        handleFirestoreError(error, `updating word count for essay ${essayId}`);
    }
};

/**
 * Gets all essays for a specific user across all assessments
 */
export const getAllEssaysForUser = async (userId: string): Promise<any[]> => {
    try {
        const q = query(
            essaysCollection,
            where('userId', '==', userId),
            orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        handleFirestoreError(error, 'getting all essays for user');
        return [];
    }
};

/**
 * Processes word counts for existing essays that don't have them
 */
export const processWordCountsForExistingEssays = async (
    userId: string,
    onProgress?: (current: number, total: number, essayId: string) => void
) => {
    try {
        // Get all essays for the user
        const allEssays = await getAllEssaysForUser(userId);
        if (!allEssays || allEssays.length === 0) {
            return { processed: 0, skipped: 0, errors: 0, total: 0 };
        }

        // Filter essays that need word count processing
        const essaysNeedingWordCount = allEssays.filter((essay: any) => 
            essay.transcribedText && // Has text to count
            (essay.wordCount === undefined || essay.wordCount === null) // Doesn't already have word count
        );

        let processed = 0;
        let skipped = 0;
        let errors = 0;

        for (let i = 0; i < essaysNeedingWordCount.length; i++) {
            const essay = essaysNeedingWordCount[i];
            
            if (onProgress) {
                onProgress(i + 1, essaysNeedingWordCount.length, essay.id);
            }

            try {
                const wordCount = calculateWordCount(essay.transcribedText);
                await updateEssayWordCount(essay.id, wordCount);
                processed++;
            } catch (error) {
                console.error(`Failed to process word count for essay ${essay.id}:`, error);
                errors++;
            }
        }

        skipped = allEssays.length - essaysNeedingWordCount.length;

        return { processed, skipped, errors, total: allEssays.length };
    } catch (error) {
        handleFirestoreError(error, 'processing word counts for existing essays');
        return { processed: 0, skipped: 0, errors: 1, total: 0 };
    }
};

/**
 * Processes word counts for existing essays using Gemini LLM
 * This is specifically for retroactive processing of essays that already exist
 */
export const processWordCountsWithGeminiForExistingEssays = async (
    userId: string,
    onProgress?: (current: number, total: number, essayId: string, status: string) => void
) => {
    try {
        // Dynamic import to avoid circular dependency
        const { calculateWordCountWithGemini } = await import('./geminiService');
        const { MODELS } = await import('../constants');

        // Get all essays for the user
        const allEssays = await getAllEssaysForUser(userId);
        if (!allEssays || allEssays.length === 0) {
            return { processed: 0, skipped: 0, errors: 0, total: 0, details: [] };
        }

        // Filter essays that need word count processing
        const essaysNeedingWordCount = allEssays.filter((essay: any) => 
            essay.transcribedText && // Has text to count
            (essay.wordCount === undefined || essay.wordCount === null) // Doesn't already have word count
        );

        let processed = 0;
        let skipped = 0;
        let errors = 0;
        const details: Array<{essayId: string, studentName: string, wordCount?: number, error?: string}> = [];

        // Use Gemini Flash model for faster processing
        const modelConfig = MODELS['gemini-2.5-flash'];

        for (let i = 0; i < essaysNeedingWordCount.length; i++) {
            const essay = essaysNeedingWordCount[i];
            
            if (onProgress) {
                onProgress(i + 1, essaysNeedingWordCount.length, essay.id, 'Processing with Gemini LLM...');
            }

            try {
                // Use Gemini to calculate word count
                const wordCount = await calculateWordCountWithGemini(essay.transcribedText, modelConfig);
                await updateEssayWordCount(essay.id, wordCount);
                
                details.push({
                    essayId: essay.id,
                    studentName: essay.studentName || 'Unknown',
                    wordCount
                });
                
                processed++;
                
                if (onProgress) {
                    onProgress(i + 1, essaysNeedingWordCount.length, essay.id, `Completed: ${wordCount} words`);
                }
                
                // Small delay to avoid rate limiting
                await new Promise(resolve => setTimeout(resolve, 100));
                
            } catch (error) {
                console.error(`Failed to process word count with Gemini for essay ${essay.id}:`, error);
                details.push({
                    essayId: essay.id,
                    studentName: essay.studentName || 'Unknown',
                    error: error instanceof Error ? error.message : String(error)
                });
                errors++;
            }
        }

        skipped = allEssays.length - essaysNeedingWordCount.length;

        return { processed, skipped, errors, total: allEssays.length, details };
    } catch (error) {
        handleFirestoreError(error, 'processing word counts with Gemini for existing essays');
        return { processed: 0, skipped: 0, errors: 1, total: 0, details: [] };
    }
};

/**
 * Retroactive word count processing specifically for nick.hull.42@gmail.com
 * This function is designed to process all existing essays for this specific account
 */
export const runRetroactiveWordCountForNickHull = async (
    onProgress?: (current: number, total: number, essayId: string, status: string) => void
) => {
    const TARGET_USER_EMAIL = 'nick.hull.42@gmail.com';
    
    try {
        console.log(`Starting retroactive word count processing for ${TARGET_USER_EMAIL}...`);
        
        if (onProgress) {
            onProgress(0, 0, '', 'Initializing retroactive processing...');
        }

        // Process essays using Gemini LLM
        const result = await processWordCountsWithGeminiForExistingEssays(
            TARGET_USER_EMAIL,
            onProgress
        );

        console.log(`Retroactive processing completed for ${TARGET_USER_EMAIL}:`, result);
        
        return {
            ...result,
            userEmail: TARGET_USER_EMAIL,
            completedAt: new Date().toISOString()
        };
        
    } catch (error) {
        console.error(`Error during retroactive word count processing for ${TARGET_USER_EMAIL}:`, error);
        throw error;
    }
};

export const mergeJustifications = (consolidatedCriteria: any[], justifications: any[], overwrite = false) => {
    return consolidatedCriteria.map(criterion => {
        const newJustificationData = justifications?.find(j => 
            normalizeCriterionName(j.criterionName) === normalizeCriterionName(criterion.criterionName)
        );
        const finalCriterion = { ...criterion };

        if (newJustificationData) {
            if (overwrite || !finalCriterion.justification) {
                finalCriterion.justification = newJustificationData.justification;
            }
        }

        if (!finalCriterion.justification) {
            finalCriterion.justification = "";
        }
        return finalCriterion;
    });
};

export const processSingleMarkingRun = (markingRunData: MarkingRunData, assessment: any, essayText: string) => {
    if (!markingRunData || !markingRunData.criteriaResults) {
        return {
            consolidatedCriteria: [],
            finalOverallScore: null,
            finalOverallBand: null,
            consolidatedAuditData: {},
            scoringMethodUsed: 'N/A',
            scoringCalculationTrace: 'No marking run data to process.'
        };
    }
    
    const rubric = assessment.rubric;
    const criteriaResults = markingRunData.criteriaResults;
    
    // Extract criteria names and order them as they appear in the rubric
    const criteriaNames = criteriaResults.map(c => normalizeCriterionName(c.criterionName));
    const rubricLower = rubric.toLowerCase();
    const orderedCriteriaNames = [...criteriaNames].sort((a, b) => {
        const indexA = rubricLower.indexOf(a.toLowerCase());
        const indexB = rubricLower.indexOf(b.toLowerCase());
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
    });

    const consolidatedAuditData: { [key: string]: { missed: string[] } } = {};
    const consolidatedCriteria = orderedCriteriaNames.map(name => {
        const criterionResult = criteriaResults.find(c => 
            normalizeCriterionName(c.criterionName) === name
        );
        
        if (!criterionResult) {
            return { criterionName: name, score: 0, selectedBand: "N/A" };
        }

        // For audit data, collect any missing descriptors
        const topBandLabel = extractBandOrder(rubric)[0];
        const missedDescriptors = criterionResult.selectedBand === topBandLabel
            ? criterionResult.topBandAudit?.missingDescriptors || []
            : [];

        consolidatedAuditData[name] = { missed: [...new Set(missedDescriptors)] };

        // Coerce to a real number: a missing or non-numeric score would otherwise
        // turn the weighted sum (score * weight) into NaN and wipe the final score.
        const numericScore = Number(criterionResult.score);
        return {
            criterionName: criterionResult.criterionName,
            score: Number.isFinite(numericScore) ? numericScore : 0,
            selectedBand: criterionResult.selectedBand,
            justification: criterionResult.justification || ""
        };
    });

    let finalOverallScore: number | null = null;
    let scoringMethodUsed = 'N/A';
    let scoringCalculationTrace = 'Calculation trace could not be generated.';

    const numCriteria = consolidatedCriteria.length;
    const scoresFromCriteria = consolidatedCriteria.map(c => c.score);
    
    if (numCriteria > 0) {
        const overallMaxScore = assessment.totalMarks || getRubricMaxScore(rubric);
        const hasStructuredCriteria = assessment.criteria && 
            Array.isArray(assessment.criteria) && 
            assessment.criteria.length > 0;

        if (hasStructuredCriteria) {
            scoringMethodUsed = 'Teacher-Defined Weighted Average';
            console.log("Applying user-defined weighted scoring logic.");

            const weightMap = new Map(assessment.criteria.map((c: {name: string, weight: number}) => [
                normalizeCriterionName(c.name),
                c.weight
            ]));

            let trace = `Calculation Method: ${scoringMethodUsed}\n\n`;

            const weightsUsed: Array<{ criterionName: string; normalized: string; weightPercent: number }> = [];

            const weightedSum = consolidatedCriteria.reduce((sum, c) => {
                const normalized = normalizeCriterionName(c.criterionName);
                const rawWeightPercent = weightMap.get(normalized);
                const weightPercent = typeof rawWeightPercent === 'number' ? rawWeightPercent : 0;
                const weight = Number(weightPercent) / 100;
                const weightedScore = c.score * weight;

                weightsUsed.push({ criterionName: c.criterionName, normalized, weightPercent });

                trace += `Criterion: "${c.criterionName}"\n`;
                trace += `  - Normalized Key: "${normalized}"\n`;
                trace += `  - AI Score: ${c.score}\n`;
                trace += `  - Weight: ${weightPercent}%\n`;
                trace += `  - Weighted Contribution: ${c.score} * ${weight} = ${weightedScore.toFixed(2)}\n\n`;

                return sum + weightedScore;
            }, 0);

            // Guardrail: if we failed to match *any* weights (common when criterion names differ by punctuation),
            // fall back to an equal-weight average instead of returning 0.
            const totalMatchedWeight = weightsUsed.reduce((sum, w) => sum + (Number(w.weightPercent) || 0), 0);
            if (totalMatchedWeight <= 0) {
                const msg = `[ScoringWarning] No criterion weights matched by name; falling back to equal weighting. This usually means the AI returned criterion names that don't exactly match the saved assessment criteria.`;
                console.warn(msg, {
                    assessmentCriteria: (assessment.criteria || []).map((c: any) => ({
                        name: c?.name,
                        normalized: normalizeCriterionName(c?.name),
                        weight: c?.weight
                    })),
                    aiCriteria: weightsUsed
                });

                trace += `WARNING: No weights matched by criterion name. Falling back to equal weighting across ${consolidatedCriteria.length} criteria.\n\n`;

                const equalWeight = consolidatedCriteria.length > 0 ? 1 / consolidatedCriteria.length : 0;
                const fallbackWeightedSum = consolidatedCriteria.reduce((sum, c) => sum + c.score * equalWeight, 0);

                trace += `Equal weight per criterion: ${(equalWeight * 100).toFixed(2)}%\n`;
                trace += `Sum of Equal-Weighted Contributions: ${fallbackWeightedSum.toFixed(2)}\n`;

                const clampedFallback = clamp(fallbackWeightedSum, 0, overallMaxScore);
                if (clampedFallback !== fallbackWeightedSum) {
                    trace += `Clamped to assessment max score (${overallMaxScore}): ${clampedFallback.toFixed(2)}\n`;
                }

                finalOverallScore = Math.round(clampedFallback);
                trace += `Rounded to nearest whole number: ${finalOverallScore}\n`;

                scoringMethodUsed = 'Equal Weight Fallback (Name Mismatch)';
                scoringCalculationTrace = trace;
            } else {
                trace += `Sum of Weighted Contributions: ${weightedSum.toFixed(2)}\n`;

                const clampedScore = clamp(weightedSum, 0, overallMaxScore);
                if (clampedScore !== weightedSum) {
                    trace += `Clamped to assessment max score (${overallMaxScore}): ${clampedScore.toFixed(2)}\n`;
                }

                finalOverallScore = Math.round(clampedScore);
                trace += `Rounded to nearest whole number: ${finalOverallScore}\n`;

                scoringCalculationTrace = trace;
            }

        } else {
            scoringMethodUsed = 'Simple Average (Fallback)';
            console.warn("Structured criteria with teacher-defined weights not found. Falling back to a simple average of criterion scores.");
            
            const sumOfScores = scoresFromCriteria.reduce((sum, score) => sum + score, 0);
            const averageScore = numCriteria > 0 ? sumOfScores / numCriteria : 0;
            let trace = `Fell back to simple average. Sum: ${sumOfScores}, Count: ${numCriteria}, Average: ${averageScore.toFixed(2)}\n`;

            const clampedScore = clamp(averageScore, 0, overallMaxScore);
            if (clampedScore !== averageScore) {
                trace += `Clamped to assessment max score (${overallMaxScore}): ${clampedScore.toFixed(2)}\n`;
            }

            finalOverallScore = Math.round(clampedScore);
            trace += `Rounded to nearest whole number: ${finalOverallScore}\n`;
            scoringCalculationTrace = trace;
        }
    }

    const bands = extractBandOrder(rubric);
    const finalOverallBand = bands.find(b => {
        const range = parseBandRange(b);
        return finalOverallScore !== null && 
            range && 
            finalOverallScore >= range.min && 
            finalOverallScore <= range.max;
    }) || "N/A";
    
    const finalBandRange = parseBandRange(finalOverallBand);
    if (finalOverallScore !== null && finalBandRange) {
        const originalScore = finalOverallScore;
        const clampedScore = clamp(finalOverallScore, finalBandRange.min, finalBandRange.max);
        if (originalScore !== clampedScore) {
            console.warn(`[BUG FIX APPLIED] Final score was inconsistent with its band. Clamped score from ${originalScore.toFixed(2)} to ${clampedScore.toFixed(2)} to fit within the "${finalOverallBand}" band.`);
            scoringMethodUsed += ' (Clamped)';
            finalOverallScore = clampedScore;
        }
    }

    return {
        consolidatedCriteria,
        finalOverallScore,
        finalOverallBand,
        consolidatedAuditData,
        scoringMethodUsed,
        scoringCalculationTrace,
    };
};

// Keep the old function name for backward compatibility, but now it just calls the simplified version
export const consolidateMarkingRuns = (allMarkingRunsData: MarkingRunData[], assessment: any, essayText: string) => {
    // For single run processing, just use the first (and only) run
    if (!allMarkingRunsData || allMarkingRunsData.length === 0) {
        return {
            consolidatedCriteria: [],
            finalOverallScore: null,
            finalOverallBand: null,
            consolidatedAuditData: {},
            scoringMethodUsed: 'N/A',
            scoringCalculationTrace: 'No runs to process.'
        };
    }
    
    return processSingleMarkingRun(allMarkingRunsData[0], assessment, essayText);
};
